import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlass,
  faCalendarDays,
  faSliders,
} from "@fortawesome/free-solid-svg-icons";
import { fetchArticlesPagination } from "../../../api/Articles";
import { subscribeMarketingNewsletter } from "../../../api/SubscribeNewsletter";
import { validateEmail } from "../../shared/Utils/ValidateEmail";
import { errorHandler } from "../../shared/Utils/ErrorHandler";
import ArticlePage from "./ArticlesPage";

const videos = [
  {
    title: "Navigating the Nonprofit Dashboard",
    duration: "2:33",
    videoId: "fb7luHl9GO8",
    src: "https://www.youtube.com/embed/fb7luHl9GO8?list=PLELFenzodbTGgZvPRlONBqvmTKLmpNaHY",
  },
  {
    title: "Creating Animal Campaigns",
    duration: "3:16",
    videoId: "S1yLV5g3KXA",
    src: "https://www.youtube.com/embed/S1yLV5g3KXA?list=PLELFenzodbTGgZvPRlONBqvmTKLmpNaHY",
  },
  {
    title: "Campaign Modifications & Updates",
    duration: "2:15",
    videoId: "rnZonX28mHs",
    src: "https://www.youtube.com/embed/rnZonX28mHs?list=PLELFenzodbTGgZvPRlONBqvmTKLmpNaHY",
  },
  {
    title: "Campaign Modifications & Updates",
    duration: "2:15",
    videoId: "rnZonX28mHs",
    src: "https://www.youtube.com/embed/rnZonX28mHs?list=PLELFenzodbTGgZvPRlONBqvmTKLmpNaHY",
  },
];

const VIDEOS_VISIBLE = 4;
const ARTICLES_VISIBLE = 3;

const ImagePlaceholder = ({ className }) => (
  <div className={`bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center ${className}`}>
    <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
    </svg>
  </div>
);

function ArticlesAndVideos() {
  const [articles, setArticles] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [videoIndex, setVideoIndex] = useState(0);
  const [articleIndex, setArticleIndex] = useState(0);
  const [openArticle, setOpenArticle] = useState(null);
  const [subscribe, setSubscribe] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState("");

  useEffect(() => {
    fetchArticlesPagination(1, "")
      .then((r) => setArticles(r.results))
      .catch(() => {});
  }, []);

  const handleSearch = async () => {
    const r = await fetchArticlesPagination(1, searchParam);
    setArticles(r.results);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email.");
      return;
    }
    try {
      await subscribeMarketingNewsletter({ email, active: false });
      setEmailSuccess("Thanks! You're subscribed!");
      setEmail("");
      setEmailError("");
    } catch (err) {
      setEmailError(errorHandler(err));
    }
  };

  const visibleVideos = videos.slice(videoIndex, videoIndex + VIDEOS_VISIBLE);
  const visibleArticles = articles.slice(articleIndex, articleIndex + ARTICLES_VISIBLE);

  if (openArticle) {
    return (
      <div className="flex-1 bg-gray-100 min-h-screen p-8">
        <ArticlePage article={openArticle} onClose={() => setOpenArticle(null)} />
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-100 min-h-screen p-8">
      {/* Header */}
      <h1 className="!text-[30px] !font-semibold !font-poppins text-blue-navy mb-4 text-left">Articles & Videos</h1>

      {/* Search + Filter */}
      <div className="flex items-center gap-3 mb-8 max-w-2xl">
        <div className="flex items-center gap-3 bg-white border border-gray-400 rounded-full px-5 py-[2px] flex-1">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="text-gray-400" size="sm" />
          <input
            type="text"
            placeholder="Search"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="flex-1 outline-none border-none bg-transparent text-sm text-gray-700 placeholder-gray-400"
          />
        </div>
        <button className="flex items-center gap-2 border border-gray-300 rounded-full px-4 py-2.5 text-sm text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap">
          <FontAwesomeIcon icon={faSliders} size="sm" />
          Filter
        </button>
      </div>

      {/* Instructional Videos */}
      <div className="mb-10">
        <div className="flex items-start justify-between mb-3">
          <div className="text-left">
            <h2 className="!text-[20px] !font-bold text-gray-900">Instructional Videos</h2>
            <p className="!text-sm text-gray-500">Guides on how to use Sponsor A Pet's platform</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setVideoIndex(Math.max(0, videoIndex - 1))}
              className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 bg-white"
            >
              <FontAwesomeIcon icon={faChevronLeft} size="xs" className="text-gray-600" />
            </button>
            <button
              onClick={() => setVideoIndex(Math.min(videos.length - VIDEOS_VISIBLE, videoIndex + 1))}
              className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 bg-white"
            >
              <FontAwesomeIcon icon={faChevronRight} size="xs" className="text-gray-600" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {visibleVideos.map((v, i) => (
            <a
              key={i}
              href={v.src}
              target="_blank"
              rel="noreferrer"
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow block"
            >
              <img
                src={`https://img.youtube.com/vi/${v.videoId}/mqdefault.jpg`}
                alt={v.title}
                className="w-full aspect-video object-cover"
                onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
              />
              <ImagePlaceholder className="w-full aspect-video hidden" />
              <div className="p-4 text-left">
                <p className="!font-bold !text-sm text-gray-900 mb-2 leading-snug">{v.title}</p>
                <p className="!text-xs text-gray-500">▶ {v.duration}</p>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Articles */}
      <div className="mb-10">
        <div className="flex items-start justify-between mb-3">
          <div className="text-left">
            <h2 className="!text-[20px] !font-bold text-gray-900">Articles</h2>
            <p className="!text-sm text-gray-500">Nonprofit Growth Guides, Strategies, and more</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setArticleIndex(Math.max(0, articleIndex - ARTICLES_VISIBLE))}
              className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 bg-white"
            >
              <FontAwesomeIcon icon={faChevronLeft} size="xs" className="text-gray-600" />
            </button>
            <button
              onClick={() => setArticleIndex(Math.min(articles.length - ARTICLES_VISIBLE, articleIndex + ARTICLES_VISIBLE))}
              className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 bg-white"
            >
              <FontAwesomeIcon icon={faChevronRight} size="xs" className="text-gray-600" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {visibleArticles.map((a, i) => (
            <div
              key={i}
              onClick={() => setOpenArticle(a)}
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            >
              {a.image ? (
                <img src={a.image} alt={a.title} className="w-full h-44 object-cover" />
              ) : (
                <ImagePlaceholder className="w-full h-44" />
              )}
              <div className="p-4 text-left">
                <p className="!font-bold !text-sm text-gray-900 mb-2 leading-snug">{a.title}</p>
                <p className="!text-xs text-gray-500 flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faCalendarDays} size="xs" />
                  {new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sponsor a Pet Talks */}
      <div className="bg-blue-navy rounded-2xl flex justify-between items-end overflow-visible relative">
        <div className="flex flex-col gap-3 p-10 text-left">
          <h2 className="!text-[32px] !font-bold text-white">Sponsor a Pet Talks</h2>
          <p className="text-white !text-[15px] !font-semibold">Check out the latest articles and videos on various topics!</p>
          {subscribe ? (
            <form onSubmit={handleSubscribe} className="flex flex-col gap-2 mt-2">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                  placeholder="Email"
                  className="rounded-full px-4 py-2 text-sm outline-none text-gray-700"
                />
                <button type="submit" className="bg-white text-blue-navy rounded-full px-5 py-2 text-sm !font-bold hover:bg-gray-100">
                  Subscribe →
                </button>
              </div>
              {emailError && <p className="text-red-300 text-xs">{emailError}</p>}
              {emailSuccess && <p className="text-green-300 text-xs">{emailSuccess}</p>}
            </form>
          ) : (
            <button
              onClick={() => setSubscribe(true)}
              className="mt-2 bg-white text-gray-900 rounded-2xl px-6 py-2.5 !text-sm !font-semibold hover:bg-gray-100 w-fit"
            >
              Subscribe
            </button>
          )}
        </div>
        <img
          src="https://sap-app-prod.s3.us-west-2.amazonaws.com/free-resources/pet-talks-dog.png"
          alt="Dog"
          className="h-64 object-contain self-start -mt-8 mr-8"
        />
      </div>
    </div>
  );
}

export default ArticlesAndVideos;
